import { useDynamicModal } from 'components/DynamicModalProvider/useDynamicModal';
import SimpleMenu from 'components/SimpleMenu';
import { useEditRealmModal } from 'SandboxModule/hooks/useEditRealmModal';
import { useDestroyRealmsModal } from 'SandboxModule/hooks/useDestroyRealmsModal';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Realm } from 'types';
import { RealmTableView, RealmTypes } from 'RealmModule/constants/realm';
import { useAddServiceToRealmModal } from 'RealmModule/hooks/useAddServiceToRealmModal';
import { useForceCleanUpSubscriptionModal } from 'RealmModule/hooks/useForceCleanUpSubscriptionModal';
import { useInitAggregateInfra } from 'SandboxModule/hooks/useInitAggregateInfra';
import { useDestroyAggregateInfra } from 'SandboxModule/hooks/useDestroyAggregateInfra';
import useSandboxes, { SandboxIsoStateIds } from 'SandboxModule/hooks/useSandboxes';
import { useAppDispatch } from 'redux/hooks';
import { useNotification } from 'hooks/useNotification';
import { checkIsSandboxDestroyable, checkIsSandboxInitializable } from 'utils';
import { initializeSandbox } from 'thunk';
import * as constants from '@constants';

/**
 * A realm that has dropped out of the list entirely cannot be verified as
 * destroyable, so it does not get destroyed.
 */
const checkIsRealmDestroyable = (realm: Realm | undefined) =>
  !!realm && checkIsSandboxDestroyable(realm);

export const useRealmTableBulkActionMenu = ({
  realmType,
  tableView,
}: {
  realmType: RealmTypes;
  /**
   * The tab the table is showing. Archived realms get a teardown-oriented menu:
   * Edit and Add Service are meaningless once a realm is archived, and Destroy
   * becomes Retry Destroy.
   */
  tableView: RealmTableView;
}) => {
  const isArchivedView = tableView === RealmTableView.Archived;
  const [checkedSandboxes, setCheckedSandboxesSet] = useState<Set<string>>(new Set());
  const { getSandbox, fetchSandboxes } = useSandboxes();
  const dispatch = useAppDispatch();
  const { showSuccess, showError } = useNotification();
  const selectedRealmIdsArray = Array.from(checkedSandboxes);
  const realmId = selectedRealmIdsArray[0];
  const realm = getSandbox(realmId);
  const tenantName = realm?.tenant || '';
  // checkIsSandboxDestroyable gates the row checkbox at render time only, and the
  // selection Set outlives the realm list it was built from: a row checked while
  // PartiallyRemoved can flip to Removed underneath it (a background fetchSandboxes,
  // or another admin finishing the teardown) and stay checked. The menu therefore
  // re-filters the selection twice: here, to decide whether Destroy renders enabled,
  // and again at click time through the ref below.
  const destroyableRealmIdsArray = selectedRealmIdsArray.filter((id) =>
    checkIsRealmDestroyable(getSandbox(id)),
  );

  // A menu can sit open across a realm-list refetch, and the click handler closes
  // over the getSandbox of the render that built it. Route the click-time re-check
  // through a ref so it reads the CURRENT realm list instead of that stale one.
  const getSandboxRef = useRef(getSandbox);
  useEffect(() => {
    getSandboxRef.current = getSandbox;
  }, [getSandbox]);

  const filterDestroyableRealmIds = useCallback(
    (realmIds: Realm['realm_id'][]) =>
      realmIds.filter((id) => checkIsRealmDestroyable(getSandboxRef.current(id))),
    [],
  );

  const { openModal } = useDynamicModal();
  const { openEditRealmModal } = useEditRealmModal();
  const { openDestroyRealmsModal } = useDestroyRealmsModal();
  const { openAddServiceToRealmModal } = useAddServiceToRealmModal();
  const { openForceCleanUpSubscriptionModal } = useForceCleanUpSubscriptionModal();
  const { initAggregateInfra } = useInitAggregateInfra();
  const { destroyAggregateInfra } = useDestroyAggregateInfra();

  const selectRealm = ({ realmId }: { realmId: Realm['realm_id'] }) => {
    setCheckedSandboxesSet((prev) => {
      const isChecked = prev.has(realmId);
      if (isChecked) {
        prev.delete(realmId);
      } else {
        prev.add(realmId);
      }

      return new Set(prev);
    });
  };

  const clearAll = () => {
    setCheckedSandboxesSet(new Set());
  };

  // The hook stays mounted across the Active/Archived toggle, so a selection made
  // in one view would otherwise stay checked in the other — where the menu offers a
  // different, destructive set of actions (Retry Destroy, Force Clean Up) that would
  // then run against a realm the other tab holds.
  useEffect(() => {
    setCheckedSandboxesSet(new Set());
  }, [tableView]);

  const onSuccess = useCallback(() => {
    clearAll();
  }, []);

  // Guards against duplicate initialize requests while one is still pending
  // (the menu can be reopened before the realm status refetch lands)
  const [initializingRealmIds, setInitializingRealmIds] = useState<Set<string>>(new Set());

  const handleInitializeRealm = useCallback(
    ({ realmId: realmIdToInitialize }: { realmId: Realm['realm_id'] }) => {
      // Get the tenant from the realm itself, not the global selection
      const realmTenant = getSandbox(realmIdToInitialize)?.tenant || '';

      setInitializingRealmIds((prev) => new Set(prev).add(realmIdToInitialize));
      const clearInitializing = () =>
        setInitializingRealmIds((prev) => {
          const next = new Set(prev);
          next.delete(realmIdToInitialize);
          return next;
        });

      dispatch(
        initializeSandbox({
          realmId: realmIdToInitialize,
          tenant: realmTenant,
          onSuccess: ({
            response,
          }: {
            response: { data?: { status?: number; message?: string } };
          }) => {
            const { data } = response;
            clearInitializing();

            if (data?.status && data.status < 300) {
              fetchSandboxes(SandboxIsoStateIds.SandboxTable);
              fetchSandboxes(SandboxIsoStateIds.SandboxCreationTimer);
              showSuccess(`Successfully initialized ${realmIdToInitialize}`);
              onSuccess();
            } else {
              showError(data?.message || 'Failed to initialize realm');
            }
          },
          onFailure: ({ error }: { error: any }) => {
            clearInitializing();
            const apiMessage = error?.data?.message || error?.message;
            const apiMessageSuffix = apiMessage ? `: ${apiMessage}` : '';
            showError(`Failed to initialize ${realmIdToInitialize}${apiMessageSuffix}`);
          },
        }),
      );
    },
    [dispatch, fetchSandboxes, getSandbox, onSuccess, showError, showSuccess],
  );

  const openRealmTableBulkSelectMenu = useCallback(
    ({ target }: { target: HTMLElement }) => {
      // Calculate a stable viewport-based position right under the clicked element
      const rect = target.getBoundingClientRect();
      const anchorPosition = {
        top: Math.round(rect.bottom),
        left: Math.round(rect.left),
      } as const;
      const hasStaleSelection = !!selectedRealmIdsArray.length && !destroyableRealmIdsArray.length;
      const destroyRealmsItem = {
        label: isArchivedView ? 'Retry Destroy' : `Destroy ${realmType}`,
        onClick: () => {
          // Re-filter here rather than trusting the array captured when the menu
          // was built — the statuses can move while the menu sits open.
          const realmIdsToDestroy = filterDestroyableRealmIds(selectedRealmIdsArray);

          if (!realmIdsToDestroy.length) {
            showError(
              `No selected ${realmType} can be destroyed — their teardown has already finished.`,
            );
            return;
          }

          openDestroyRealmsModal({
            realmIds: realmIdsToDestroy,
            onDestroyRealmSuccess: onSuccess,
            realmType,
            isRetry: isArchivedView,
          });
        },
        disableButton: hasStaleSelection,
        tooltip: hasStaleSelection ? 'Teardown has already finished for the selected realms' : '',
        value: 'destroy',
      };

      const viewActivityLogItem = {
        label: 'View activity log',
        onClick: () => {
          // Open activity log in new tab with tenant and realmId pre-populated
          const params = new URLSearchParams({
            tenant: tenantName,
            realmId: realmId,
          });
          window.open(
            `${constants.ACTIVITY_LOG_PATH}?${params.toString()}`,
            '_blank',
            'noopener,noreferrer',
          );
        },
        disableButton: checkedSandboxes.size !== 1,
        tooltip: checkedSandboxes.size > 1 ? 'Cannot view activity log for multiple realms' : '',
      };

      const destroyAggregateInfraItem = {
        label: 'Destroy Aggregate Infra',
        onClick: () => {
          destroyAggregateInfra({
            realmId: realmId,
            tenant: tenantName,
          });
        },
        disableButton: checkedSandboxes.size !== 1,
      };

      const clearAllItem = {
        label: 'Clear All',
        onClick: () => {
          clearAll();
        },
        disableButton: !checkedSandboxes.size,
      };

      // Archived: teardown only. Retry Destroy re-runs the destroy that stalled,
      // and — for subscriptions, whose databases and secrets destroyRealm never
      // touches — Force Clean Up drops what is left behind.
      const archivedMenuItems = [
        destroyRealmsItem,
        ...(realmType === RealmTypes.subscription
          ? [
              {
                label: 'Force Clean Up Subscription',
                onClick: () => {
                  // Same click-time re-check as Retry Destroy, and for a stronger
                  // reason: this one drops databases and secrets outright, with no
                  // retention window, so it must not run against a realm that
                  // reached Removed while the menu sat open.
                  const [realmIdToCleanUp] = filterDestroyableRealmIds([realmId]);

                  if (!realmIdToCleanUp) {
                    showError(
                      'Teardown has already finished for the selected subscription — nothing left to clean up.',
                    );
                    return;
                  }

                  openForceCleanUpSubscriptionModal({
                    realmId: realmIdToCleanUp,
                    onForceCleanUpSuccess: onSuccess,
                  });
                },
                disableButton: checkedSandboxes.size !== 1 || hasStaleSelection,
                tooltip:
                  checkedSandboxes.size > 1
                    ? 'Cannot force clean up multiple subscriptions'
                    : hasStaleSelection
                      ? 'Teardown has already finished for the selected realms'
                      : '',
              },
            ]
          : []),
        viewActivityLogItem,
        destroyAggregateInfraItem,
        clearAllItem,
      ];

      const activeMenuItems = [
        // Only offered when the realm failed to initialize properly and
        // needs a retry (GLX-1738)
        ...(realm && checkIsSandboxInitializable(realm.status)
          ? [
              {
                label: 'Initialize',
                onClick: () => {
                  handleInitializeRealm({ realmId });
                },
                disableButton: checkedSandboxes.size !== 1 || initializingRealmIds.has(realmId),
                tooltip:
                  checkedSandboxes.size > 1
                    ? 'Cannot initialize multiple realms'
                    : initializingRealmIds.has(realmId)
                      ? 'Initialization in progress'
                      : '',
              },
            ]
          : []),
        destroyRealmsItem,
        {
          label: `Edit ${realmType}`,
          onClick: () => {
            openEditRealmModal({
              realmId,
              realmType,
              onEditRealmSuccess: () => {
                clearAll();
              },
            });
          },
          disableButton: checkedSandboxes.size !== 1,
        },
        ...(realmType === RealmTypes.subscription
          ? [
              {
                label: 'Add Service',
                onClick: () => {
                  openAddServiceToRealmModal({
                    realmId,
                  });
                },
                disableButton: checkedSandboxes.size !== 1,
              },
            ]
          : []),
        viewActivityLogItem,
        {
          label: 'Init Aggregate Infra',
          onClick: () => {
            initAggregateInfra({
              realmId: realmId,
              tenant: tenantName,
            });
          },
          disableButton: checkedSandboxes.size !== 1,
        },
        destroyAggregateInfraItem,
        clearAllItem,
      ];

      const menuItems = isArchivedView ? archivedMenuItems : activeMenuItems;

      openModal({
        modal: ({ isOpen: _isOpen, toggle }) => {
          return (
            <SimpleMenu
              // Keep anchorEl for focus management, but rely on explicit position for stability
              anchorEl={target}
              anchorPosition={anchorPosition}
              items={menuItems}
              handleClose={toggle}
              hideAnchor={true}
            />
          );
        },
      });
    },

    [
      realmType,
      isArchivedView,
      checkedSandboxes.size,
      openModal,
      openDestroyRealmsModal,
      selectedRealmIdsArray,
      destroyableRealmIdsArray,
      filterDestroyableRealmIds,
      showError,
      onSuccess,
      openEditRealmModal,
      realmId,
      realm,
      handleInitializeRealm,
      initializingRealmIds,
      openAddServiceToRealmModal,
      openForceCleanUpSubscriptionModal,
      initAggregateInfra,
      tenantName,
      destroyAggregateInfra,
    ],
  );

  return {
    openRealmTableBulkSelectMenu,
    setCheckedSandboxesSet,
    checkedSandboxes,
    selectRealm,
  };
};
